import React from 'react';
import { Form } from 'react-bootstrap';

const SurveyQuestion = ({ 
  question, 
  value, 
  selectedOptions = [], 
  onChange, 
  error, 
  questionNumber 
}) => {

  const handleInputChange = (e) => {
    onChange(e.target.value);
  };

  const handleCheckboxChange = (optionId, checked) => {
    let newSelectedOptions;
    if (checked) {
      newSelectedOptions = [...selectedOptions, optionId];
    } else {
      newSelectedOptions = selectedOptions.filter(id => id !== optionId);
    }
    onChange(null, newSelectedOptions);
  };

  const handleRadioChange = (optionId) => {
    onChange(optionId);
  };

  const renderQuestionInput = () => {
    switch (question.questionType) {
      case 'TEXT_FIELD':
        return (
          <Form.Control
            type="text"
            value={value || ''}
            onChange={handleInputChange}
            placeholder={question.placeholderText || 'Enter your answer'}
            isInvalid={!!error}
          />
        );

      case 'TEXT_AREA':
        return (
          <Form.Control
            as="textarea"
            rows={4}
            value={value || ''}
            onChange={handleInputChange}
            placeholder={question.placeholderText || 'Enter your answer'}
            isInvalid={!!error}
          />
        );

      case 'EMAIL':
        return (
          <Form.Control
            type="email"
            value={value || ''}
            onChange={handleInputChange}
            placeholder={question.placeholderText || 'Enter your email'}
            isInvalid={!!error}
          />
        );

      case 'NUMBER':
        return (
          <Form.Control
            type="number"
            value={value || ''}
            onChange={handleInputChange}
            placeholder={question.placeholderText || 'Enter a number'}
            isInvalid={!!error}
          />
        );

      case 'DATE':
        return (
          <Form.Control
            type="date"
            value={value || ''}
            onChange={handleInputChange}
            isInvalid={!!error}
          />
        );

      case 'DROPDOWN':
        return (
          <Form.Select
            value={value || ''}
            onChange={handleInputChange}
            isInvalid={!!error}
          >
            <option value="">Select an option...</option>
            {question.options.map(option => (
              <option key={option.id} value={option.id}>
                {option.optionText}
              </option>
            ))}
          </Form.Select>
        );

      case 'RADIO':
      case 'SINGLE_CHOICE':
        return (
          <div>
            {question.options.map(option => (
              <Form.Check
                key={option.id}
                type="radio"
                id={`question-${question.id}-option-${option.id}`}
                name={`question-${question.id}`}
                label={option.optionText}
                checked={value === option.id}
                onChange={() => handleRadioChange(option.id)}
                className="mb-2"
              />
            ))}
            {error && <div className="invalid-feedback d-block">{error}</div>}
          </div>
        );

      case 'CHECKBOX':
      case 'MULTIPLE_CHOICE':
        return (
          <div>
            {question.options.map(option => (
              <Form.Check
                key={option.id}
                type="checkbox"
                id={`question-${question.id}-option-${option.id}`}
                label={option.optionText}
                checked={selectedOptions.includes(option.id)}
                onChange={(e) => handleCheckboxChange(option.id, e.target.checked)}
                className="mb-2"
              />
            ))}
            {error && <div className="invalid-feedback d-block">{error}</div>}
          </div>
        );

      case 'YES_NO':
        return (
          <div>
            <Form.Check
              type="radio"
              id={`question-${question.id}-yes`}
              name={`question-${question.id}`}
              label="Yes"
              checked={value === 'yes'}
              onChange={() => handleRadioChange('yes')}
              className="mb-2"
            />
            <Form.Check
              type="radio"
              id={`question-${question.id}-no`}
              name={`question-${question.id}`}
              label="No"
              checked={value === 'no'}
              onChange={() => handleRadioChange('no')}
              className="mb-2"
            />
            {error && <div className="invalid-feedback d-block">{error}</div>}
          </div>
        );

      case 'RATING':
        return (
          <div>
            <Form.Range
              min="1"
              max="10"
              value={value || 5}
              onChange={handleInputChange}
              className="mb-2"
            />
            <div className="d-flex justify-content-between">
              <small className="text-muted">1 (Poor)</small>
              <small className="text-muted">Current: {value || 5}</small>
              <small className="text-muted">10 (Excellent)</small>
            </div>
            {error && <div className="invalid-feedback d-block">{error}</div>}
          </div>
        );

      default:
        return (
          <Form.Control
            type="text"
            value={value || ''}
            onChange={handleInputChange}
            placeholder={question.placeholderText || 'Enter your answer'}
            isInvalid={!!error}
          />
        );
    }
  };

  return (
    <div className="survey-question">
      <Form.Group>
        <Form.Label className="fw-bold">
          <span className="question-number me-2">
            {questionNumber}.
          </span>
          {question.questionText}
          {question.required && (
            <span className="text-danger ms-1">*</span>
          )}
        </Form.Label>
        
        {question.description && (
          <div className="text-muted small mb-2">
            {question.description}
          </div>
        )}

        {renderQuestionInput()}

        {error && (
          <Form.Control.Feedback type="invalid" className="d-block">
            {error}
          </Form.Control.Feedback>
        )}
      </Form.Group>

      <style jsx>{`
        .survey-question {
          border-left: 3px solid #e9ecef;
          padding-left: 1rem;
          margin-bottom: 1.5rem;
        }
        
        .survey-question:hover {
          border-left-color: #007bff;
        }
        
        .question-number {
          color: #007bff;
          font-weight: bold;
        }
        
        .form-check {
          padding-left: 1.5rem;
        }
        
        .form-check-input:checked {
          background-color: #007bff;
          border-color: #007bff;
        }
        
        .form-range::-webkit-slider-thumb {
          background-color: #007bff;
        }
        
        .form-range::-moz-range-thumb {
          background-color: #007bff;
          border: none;
        }
      `}</style>
    </div>
  );
};

export default SurveyQuestion;